/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { connect } from 'react-redux'

import Feature from '@modules/utils/feature'
import withBillingDetailsProvider from '@modules/billing-lib/billingDetails/BillingDetailsProvider/withBillingDetailsProvider'
import withBillingDetails from '@modules/billing-lib/billingDetails/withBillingDetails'

import { fetchOrganizationInvitations, fetchOrganizationMemberships } from '@/actions/organizations'
import { isFeatureActivated } from '@/selectors'
import { fetchSaasOrganization } from '@/actions/saasOrganizations'
import { getOrganizationMemberRows } from '@/components/Organization/OrganizationMembers/lib'
import { getSaasOrganization } from '@/reducers/saasOrganizations'
import {
  fetchOrganizationMembershipsRequest,
  fetchOrganizationInvitationsRequest,
  fetchSaasOrganizationRequest,
} from '@/reducers/asyncRequests/registry'

import Organization from './Organization'
import { getOrganizationDeploymentsSearchResults } from './OrganizationDeployments/lib'
import { StateProps, DispatchProps, ConsumerProps } from './types'

import type { ThunkDispatch, ReduxState } from '@/types/redux'

const mapStateToProps = (
  state: ReduxState,
  {
    match: {
      params: { organizationId, selectedTabId },
    },
  }: ConsumerProps,
): StateProps => {
  const organizationMemberRows = getOrganizationMemberRows(state, organizationId)

  // explicitly passing organizationId so the withBillingDetailsProvider HOC gets the necessary props

  return {
    organizationId,
    saasOrganization: getSaasOrganization(state, organizationId),
    fetchSaasOrganizationRequest: fetchSaasOrganizationRequest(state, organizationId),
    searchResults: getOrganizationDeploymentsSearchResults(state, organizationId),
    selectedTabId,
    lookupSaasUsers: isFeatureActivated(state, Feature.lookupSaasUsers),
    organizationMemberRows,
    fetchOrganizationInvitationsRequest: fetchOrganizationInvitationsRequest(state, organizationId),
    fetchOrganizationMembersRequest: fetchOrganizationMembershipsRequest(state, organizationId),
    serverlessFeature: isFeatureActivated(state, Feature.serverless),
  }
}

const mapDispatchToProps = (
  dispatch: ThunkDispatch,
  {
    match: {
      params: { organizationId },
    },
  }: ConsumerProps,
): DispatchProps => ({
  fetchSaasOrganization: () => dispatch(fetchSaasOrganization({ organizationId })),
  fetchOrganizationMembers: () => dispatch(fetchOrganizationMemberships({ organizationId })),
  fetchOrganizationInvites: () => dispatch(fetchOrganizationInvitations({ organizationId })),
})

export default connect<StateProps, DispatchProps, ConsumerProps>(
  mapStateToProps,
  mapDispatchToProps,
)(withBillingDetailsProvider(withBillingDetails(Organization)))
